import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import SeatGrid from "@/components/SeatGrid";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { applyPosterFallback, getEventPoster } from "@/lib/eventDisplay";

export default function Booking() {
  const { type, id, showtimeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [movie, setMovie] = useState(null);
  const [showtime, setShowtime] = useState(null);
  const [seats, setSeats] = useState([]);
  const [event, setEvent] = useState(location.state?.event || null);
  const [tier, setTier] = useState(location.state?.tier || null);
  const [qty, setQty] = useState(location.state?.qty || 1);
  const [ticketItems, setTicketItems] = useState(location.state?.ticketItems || []);
  const [selectedDate, setSelectedDate] = useState(location.state?.selectedDate || location.state?.event?.date || "");
  const [processing, setProcessing] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const freeRequestId = useRef("");

  useEffect(() => {
    if (type === "movie") {
      api.get(`/movies/${id}`).then((r) => {
        setMovie(r.data);
        setShowtime((r.data.showtimes || []).find((s) => s.showtime_id === showtimeId));
      });
    } else if (type === "event" && !event) {
      api.get(`/events/${id}`).then((r) => {
        setEvent(r.data);
        setSelectedDate((current) => current || r.data.date || "");
        if (!ticketItems.length && r.data.prices?.length) {
          const first = r.data.prices[0];
          const fallbackQty = Math.max(1, Number(r.data.min_per_order) || 1);
          setTicketItems([{ category: first.tier, quantity: fallbackQty, price: Number(first.price || 0) }]);
          setTier(first.tier);
          setQty(fallbackQty);
        }
      });
    }
  }, [type, id, showtimeId, event, ticketItems.length]);

  const pricePerSeatStandard = showtime?.price_standard || 12.5;
  const pricePerSeatPremium = showtime?.price_premium || 18;
  const selectedTier = event?.prices?.find((p) => p.tier === tier);
  const normalizedTicketItems = ticketItems.length
    ? ticketItems
    : (tier ? [{ category: tier, quantity: Number(qty || 1), price: Number(selectedTier?.price || 0) }] : []);
  // First 3 rows are premium
  const total = type === "movie"
    ? seats.reduce((s, id) => s + (["A","B","C"].includes(id[0]) ? pricePerSeatPremium : pricePerSeatStandard), 0)
    : (location.state?.total || normalizedTicketItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0));
  const eventTicketCount = normalizedTicketItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const checkoutPayload = {
    item_type: type,
    item_id: type === "movie" ? movie?.movie_id : event?.event_id,
    showtime_id: type === "movie" ? showtime?.showtime_id : null,
    seats: type === "movie" ? seats : [],
    category: type === "event" ? (normalizedTicketItems.length === 1 ? normalizedTicketItems[0].category : "Mixed tickets") : null,
    quantity: type === "event" ? eventTicketCount : seats.length,
    ticket_items: type === "event" ? normalizedTicketItems : [],
    booking_date: type === "event" ? selectedDate : null,
    origin_url: window.location.origin,
  };

  useEffect(() => {
    if (!user || !checkoutPayload.item_id || (type === "movie" && seats.length === 0) || (type === "event" && eventTicketCount === 0)) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    setQuoteError("");
    api.post("/checkout/quote", checkoutPayload)
      .then(({ data }) => {
        if (!cancelled) setQuote(data);
      })
      .catch((error) => {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(error.response?.data?.detail || "Could not calculate the booking fee.");
        }
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => { cancelled = true; };
    // The payload is intentionally recalculated whenever the ticket selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, type, movie?.movie_id, showtime?.showtime_id, event?.event_id, selectedDate, seats, ticketItems, tier, qty]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md p-10 text-center">
          <h2 className="font-display text-2xl font-bold">Please sign in to continue</h2>
          <Button onClick={() => navigate("/login", { state: { from: location.pathname } })} className="mt-4 rounded-full" data-testid="signin-cta">
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  const ticketSubtotal = Number(quote?.ticket_subtotal ?? total);
  const bookingFee = Number(quote?.booking_fee || 0);
  const checkoutTotal = Number(quote?.total ?? total);
  const isFree = quote?.is_free === true;
  const feeLabel = quote
    ? `Booking fee (${Number(quote.fee_percentage || 0).toFixed(1)}% + £${(Number(quote.fee_fixed_pence || 0) / 100).toFixed(2)})`
    : "Booking fee";

  const canUseLocalDemoCheckout = () => ["127.0.0.1", "localhost"].includes(window.location.hostname);

  const createLocalDemoBooking = async (payload) => {
    const { data } = await api.post("/bookings", {
      ...payload,
      total,
      payment_method: "card",
    });
    toast.success(`Demo booking created with ${data.ticket_units?.length || data.quantity || 1} individual QR ticket${(data.ticket_units?.length || data.quantity || 1) > 1 ? "s" : ""}.`);
    navigate("/bookings");
  };

  const onPay = async () => {
    if (type === "movie" && seats.length === 0) {
      toast.error("Please pick at least one seat.");
      return;
    }
    setProcessing(true);
    const payload = checkoutPayload;
    try {
      if (isFree) {
        if (!freeRequestId.current) {
          freeRequestId.current = globalThis.crypto?.randomUUID?.().replaceAll("-", "") ||
            `rsvp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        }
        const { data } = await api.post("/checkout/free", {
          ...payload,
          request_id: freeRequestId.current,
        });
        toast.success(`RSVP confirmed. ${data.booking?.ticket_units?.length || eventTicketCount || 1} QR ticket${(data.booking?.ticket_units?.length || eventTicketCount || 1) === 1 ? "" : "s"} created.`);
        navigate("/bookings");
        return;
      }
      const { data } = await api.post("/checkout/session", payload);
      if (!data?.url) throw new Error("No checkout URL");
      // Redirect to Stripe-hosted checkout
      window.location.href = data.url;
    } catch (e) {
      if (type === "event" && canUseLocalDemoCheckout()) {
        try {
          await createLocalDemoBooking(payload);
          return;
        } catch (fallbackError) {
          toast.error(fallbackError.response?.data?.detail || "Couldn't create local demo booking.");
        }
      } else {
        toast.error(e.response?.data?.detail || "Couldn't start checkout. Try again.");
      }
      setProcessing(false);
    }
  };

  const item = type === "movie" ? movie : event;
  if (!item) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 rounded-2xl border border-border p-6">
          {type === "movie" ? (
            <>
              <div className="flex items-center gap-4">
                <img src={movie?.poster} alt="" className="h-20 w-14 rounded-lg object-cover" />
                <div>
                  <h1 className="font-display text-2xl font-extrabold">{movie?.title}</h1>
                  <p className="text-xs text-muted-foreground">{showtime?.venue} · {showtime?.city} · {showtime?.date} · {showtime?.time}</p>
                </div>
              </div>
              <div className="mt-6 -mx-2 overflow-x-auto">
                <SeatGrid onChange={setSeats} />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <img src={getEventPoster(event)} alt="" loading="lazy" decoding="async" className="h-20 w-32 rounded-lg object-cover" onError={(e) => applyPosterFallback(e, event)} />
                <div>
                  <h1 className="font-display text-2xl font-extrabold">{event?.title}</h1>
                  <p className="text-xs text-muted-foreground">{event?.venue} · {event?.city} · {selectedDate || event?.date} · {event?.time}</p>
                  <div className="mt-1 text-sm space-y-0.5">
                    {normalizedTicketItems.map((item) => (
                      <p key={item.category}>
                        {item.category} × {item.quantity}
                        <span className="block text-xs text-muted-foreground">{ticketDateLabel(item, selectedDate)}</span>
                      </p>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{eventTicketCount} individual QR code{eventTicketCount === 1 ? "" : "s"} for separate entry.</p>
                </div>
              </div>
            </>
          )}

          <div className="mt-8 space-y-3">
            <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">{isFree ? "RSVP" : "Payment"}</p>
            <div className="p-5 rounded-2xl border border-border bg-muted/30" data-testid="stripe-payment-info">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-foreground text-background">
                  <Lock className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <p className="font-bold text-sm">{isFree ? "Free RSVP registration" : "Secure checkout via Stripe"}</p>
                  <p className="text-xs text-muted-foreground">{isFree ? "No card or payment is required" : "Apple Pay · Google Pay · Visa · Mastercard · Amex"}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-primary/10 text-primary">{isFree ? "Free" : "Secure payment"}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                {isFree
                  ? "Confirm your attendance and your individual QR ticket will be available immediately in My bookings."
                  : "You'll be redirected to Stripe's hosted page to complete payment, then brought back to your e-ticket."}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border p-6 h-fit sticky top-20">
          <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Summary</p>
          <div className="mt-4 space-y-2 text-sm">
            {type === "movie" ? (
              <>
                <div className="flex justify-between"><span>Seats</span><span>{seats.length ? seats.join(", ") : "—"}</span></div>
              </>
            ) : (
              <>
                {normalizedTicketItems.map((item) => (
                  <div key={item.category} className="flex justify-between gap-3">
                    <span>
                      {item.category} × {item.quantity}
                      <span className="block text-xs text-muted-foreground">{ticketDateLabel(item, selectedDate)}</span>
                    </span>
                    <span className="shrink-0">£{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</span>
                  </div>
                ))}
              </>
            )}
            <div className="border-t border-border pt-3 flex justify-between"><span>Ticket subtotal</span><span>£{ticketSubtotal.toFixed(2)}</span></div>
            {!isFree && <div className="flex justify-between text-muted-foreground"><span>{feeLabel}</span><span>{quoteLoading ? "Calculating…" : `£${bookingFee.toFixed(2)}`}</span></div>}
            <p className="text-[11px] leading-4 text-muted-foreground">{isFree ? "No booking fee is charged for a free RSVP." : "The booking fee supports secure payment processing, QR ticketing and event support. The applicable rate is set for this event."}</p>
            {!isFree && quote?.fee_comparison_note && <p className="text-[11px] font-semibold leading-4 text-primary">{quote.fee_comparison_note}</p>}
            {quoteError && <p className="text-xs text-destructive">{quoteError}</p>}
            <div className="border-t border-border pt-3 flex justify-between font-display text-xl font-extrabold">
              <span>Total</span><span>£{checkoutTotal.toFixed(2)}</span>
            </div>
          </div>
          <Button
            onClick={onPay}
            disabled={processing || quoteLoading || !quote}
            data-testid="confirm-pay-btn"
            className="w-full rounded-full mt-5 h-11 font-bold"
          >
            {processing ? (isFree ? "Confirming…" : "Redirecting…") : quoteLoading ? "Calculating total…" : isFree ? "Confirm free RSVP" : `Pay £${checkoutTotal.toFixed(2)} →`}
          </Button>
        </div>
      </main>
    </div>
  );
}

function ticketDateLabel(item, selectedDate) {
  const dates = Array.isArray(item.valid_dates) ? item.valid_dates.filter(Boolean) : [];
  if (item.scope === "multi_day") {
    return dates.length > 1
      ? `Valid ${formatShortDate(dates[0])} to ${formatShortDate(dates[dates.length - 1])}`
      : `Valid ${formatShortDate(dates[0] || selectedDate)}`;
  }
  return `Valid ${formatShortDate(dates[0] || selectedDate)}`;
}

function formatShortDate(value) {
  if (!value) return "selected day";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
